import { formatAsFileSize, SmeCustomerData } from 'cpfr-common';
import { FileUpload, ValidatedInput } from '@cp-shared-19/frontend-ui';
import { Fieldset, FormFieldLabel, Layout, FormFieldRadioButtonGroup, RadioButton } from '@vwfs-bronson/bronson-react';
import React, { Dispatch, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormikContext } from 'formik';
import { CharlyDatepicker } from 'components/shared/charly-datepicker/CharlyDatepicker';
import { maxFileSize } from 'config';
import { CustomerType, getFormCustomerType, CustomerDataFormFields } from './utils';

export enum EditStatus {
    NOT_PERFORMED = 'NOT_PERFORMED',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
    ERROR_VIRUS = 'ERROR_VIRUS',
    NO_CHANGE_DETECTED = 'NO_CHANGE_DETECTED',
    WRONG_REASON = 'WRONG_REASON',
    SIREN_IS_MANDATORY_FOR_SME = 'SIREN_IS_MANDATORY_FOR_SME',
    ERROR_NO_EMAIL = 'EMAIL_NOT_FOUND_OR_PROVIDED',
}

export const IdentityFields: React.FC<{ setRequiredFiles: Dispatch<React.SetStateAction<boolean[]>> }> = ({
    setRequiredFiles,
}) => {
    const { t } = useTranslation(['my-profile', 'confirm-information']);
    const formik: FormikProps<CustomerDataFormFields> = useFormikContext();
    const [isDirty, setIsDirty] = useState(false);
    const [initial] = useState(formik.initialValues.identification.data);
    const fileUploadTranslationPrefix = 'identification.edit-view.file-upload';
    const [customerType, setCustomerType] = useState<CustomerType>('');

    useEffect(() => {
        setCustomerType(getFormCustomerType(formik.initialValues.identification.customerType));
        const dirty =
            initial &&
            (formik.values.identification.data.name !== initial.name ||
                formik.values.identification.data.surname !== initial.surname ||
                (formik.values.identification.data as SmeCustomerData).companyName !==
                    (initial as SmeCustomerData).companyName);
        setRequiredFiles((files) => {
            const updated = [...files];
            updated[0] = dirty;
            return updated;
        });
        setIsDirty(dirty);
    }, [initial, formik.values]);

    return (
        <>
            <Fieldset>
                {customerType === 'SME' && (
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item default="1/2" s="1/1">
                                <FormFieldLabel>{t('confirm-information:vehicle-usage')}</FormFieldLabel>
                                <FormFieldRadioButtonGroup>
                                    <RadioButton
                                        name="personalUsage"
                                        checked={formik.values.personalUsage}
                                        onChange={() => formik.setFieldValue('personalUsage', true)}
                                    >
                                        {t('confirm-information:personal-usage')}
                                    </RadioButton>
                                    <RadioButton
                                        name="personalUsage"
                                        checked={formik.values.personalUsage === false}
                                        onChange={() => formik.setFieldValue('personalUsage', false)}
                                    >
                                        {t('confirm-information:proffesional-usage')}
                                    </RadioButton>
                                </FormFieldRadioButtonGroup>
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                )}
                {customerType === 'PM' ? (
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item default="1/1" s="1/1">
                                <ValidatedInput
                                    className="c-form-field--no-floating"
                                    label={t('confirm-information:company-name')}
                                    name="identification.data.companyName"
                                    testId="name"
                                    inputId="companyName-identity-fields"
                                    type="text"
                                    aria-required={true}
                                    isMandatory={true}
                                    requiredText=" *"
                                    autocomplete="orginiaztion"
                                />
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                ) : (
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item default="1/2" s="1/1">
                                <ValidatedInput
                                    className="c-form-field--no-floating"
                                    label={t('confirm-information:first-name')}
                                    name="identification.data.name"
                                    testId="name"
                                    inputId="first-name-identity-fields"
                                    type="text"
                                    aria-required={true}
                                    isMandatory={true}
                                    requiredText=" *"
                                    autocomplete="given-name"
                                />
                            </Layout.Item>
                            <Layout.Item default="1/2" s="1/1">
                                <ValidatedInput
                                    className="c-form-field--no-floating"
                                    label={t('confirm-information:last-name')}
                                    name="identification.data.surname"
                                    testId="surname"
                                    inputId="surname-identity-fields"
                                    type="text"
                                    aria-required={true}
                                    isMandatory={true}
                                    requiredText=" *"
                                    autocomplete="family-name"
                                />
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                )}
                {customerType === 'Retail' && (
                    <Fieldset.Row>
                        <Layout>
                            <Layout.Item key="birth-date" default="1/2" s="1/1">
                                <CharlyDatepicker
                                    label={t('confirm-information:birth-date')}
                                    required={true}
                                    disabled={!!initial.birthDate}
                                    value={formik.values.identification.data.birthDate}
                                    onChange={(iso) => formik.setFieldValue('identification.data.birthDate', iso)}
                                    errorMessage={formik.errors.identification?.data?.birthDate}
                                />
                            </Layout.Item>
                        </Layout>
                    </Fieldset.Row>
                )}
            </Fieldset>
            {isDirty && (
                <Fieldset.Row>
                    <Layout>
                        <Layout.Item>
                            <FileUpload
                                name={'proofOfIdentityChange'}
                                uploadItemsLabel={''}
                                descriptionText={t(
                                    `${fileUploadTranslationPrefix}.${
                                        customerType === 'Retail'
                                            ? 'proof-of-identity-change-risky'
                                            : customerType === 'PM'
                                            ? 'evidence-for-change-m'
                                            : formik.values.personalUsage
                                            ? 'proof-of-identity-change-risky'
                                            : 'evidence-for-change-m'
                                    }`,
                                )}
                                descriptionSupplementaryText={<></>}
                                buttonText={t(`${fileUploadTranslationPrefix}.button`)}
                                cancelLabel={t('translation:editable-section-nav.cancel')}
                                fileSizeFormatter={(bytes: number): string => formatAsFileSize(bytes, 2)}
                                maxFileSize={maxFileSize}
                                multiple={false}
                                validFileFormats={['pdf']}
                                sizeError={t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                typeError={t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                sizeAndTypeError={
                                    <>
                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-size`)}
                                        <br aria-hidden="true" />
                                        {t(`${fileUploadTranslationPrefix}.validation.wrong-type`)}
                                    </>
                                }
                            />
                        </Layout.Item>
                    </Layout>
                </Fieldset.Row>
            )}
        </>
    );
};

export default IdentityFields;
